function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function extractRawMessage(raw: unknown): string | null {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      const nested =
        (parsed.error as Record<string, unknown> | undefined)?.message ??
        parsed.message ??
        (parsed.error as string | undefined);
      if (typeof nested === "string" && nested.trim()) return nested.trim();
    } catch {
      return trimmed;
    }
    return trimmed;
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const nested =
      (obj.error as Record<string, unknown> | undefined)?.message ??
      obj.message ??
      (typeof obj.error === "string" ? obj.error : undefined);
    if (typeof nested === "string" && nested.trim()) return nested.trim();
    const serialized = safeStringify(raw);
    if (serialized && serialized !== "{}") return serialized;
  }

  return null;
}

/**
 * Extract a human-readable error from OpenRouter / ai-proxy error payloads.
 * Surfaces error.metadata.raw and provider_name when the top-level message is generic.
 */
export function extractOpenRouterErrorMessage(payload: unknown): string | null {
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) return null;
    try {
      return extractOpenRouterErrorMessage(JSON.parse(trimmed));
    } catch {
      return trimmed;
    }
  }

  if (!payload || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;
  const errorField = record.error;
  const errorObj =
    errorField && typeof errorField === "object"
      ? (errorField as Record<string, unknown>)
      : typeof errorField === "string"
        ? { message: errorField }
        : undefined;

  const metadata =
    errorObj?.metadata && typeof errorObj.metadata === "object"
      ? (errorObj.metadata as Record<string, unknown>)
      : undefined;

  const parts: string[] = [];
  const providerName = metadata?.provider_name;
  if (typeof providerName === "string" && providerName.trim()) {
    parts.push(`[${providerName.trim()}]`);
  }

  const rawMessage = metadata?.raw != null ? extractRawMessage(metadata.raw) : null;
  const topMessage =
    typeof errorObj?.message === "string"
      ? errorObj.message.trim()
      : typeof record.message === "string"
        ? record.message.trim()
        : typeof errorField === "string"
          ? errorField.trim()
          : "";

  const genericMessages = new Set([
    "provider returned error",
    "openrouter upstream failure",
  ]);

  if (rawMessage && (!topMessage || genericMessages.has(topMessage.toLowerCase()))) {
    parts.push(rawMessage);
  } else if (topMessage) {
    parts.push(topMessage);
  } else if (rawMessage) {
    parts.push(rawMessage);
  }

  const details = errorObj?.details ?? record.details ?? record.detail;
  if (typeof details === "string" && details.trim() && !parts.some((p) => p.includes(details))) {
    parts.push(details.trim());
  }

  const limitSource = metadata?.limit_source;
  if (typeof limitSource === "string" && limitSource.trim()) {
    parts.push(`limit_source:${limitSource.trim()}`);
  }

  if (parts.length) return parts.join(" ");

  const candidates: unknown[] = [
    record.reason,
    errorObj?.code,
    errorField,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }

  const fallback = safeStringify(payload);
  return fallback && fallback !== "{}" ? fallback : null;
}

/** Output reservation used when a chat caller does not set max_tokens. */
export const DEFAULT_CHAT_MAX_TOKENS = 16_384;

/** Below this, a retry would not produce a useful reply. */
export const MIN_AFFORDABLE_OUTPUT_TOKENS = 512;

/**
 * OpenRouter: "You requested up to 65536 tokens, but can only afford 33729."
 * Returns the affordable output cap, or null when the sentence is absent.
 */
export function parseAffordableMaxTokens(message: string): number | null {
  const match = message.match(/can only afford\s+([\d,]+)/i);
  if (!match) return null;
  const value = Number(match[1].replace(/,/g, ""));
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.floor(value);
}

/**
 * Caller-supplied caps are kept. Omitted caps become min(model max, 16384)
 * so OpenRouter does not reserve the model's full completion length.
 */
export function resolveChatMaxTokens(
  requested: number | null | undefined,
  modelMaxCompletion: number | null | undefined,
): number {
  if (requested != null && Number.isFinite(requested) && requested > 0) {
    return Math.floor(requested);
  }
  const modelCap =
    modelMaxCompletion != null && Number.isFinite(modelMaxCompletion) && modelMaxCompletion > 0
      ? Math.floor(modelMaxCompletion)
      : DEFAULT_CHAT_MAX_TOKENS;
  return Math.min(modelCap, DEFAULT_CHAT_MAX_TOKENS);
}

/** Shown when OpenRouter rejects the call because the account balance cannot cover it. */
export const OPENROUTER_ACCOUNT_BALANCE_MESSAGE =
  "The OpenRouter account balance is too low to start this reply for this model and prompt. Deeply Smart credits are a separate balance and are not used up.";

/** Account credits, or a prompt that already costs more than the remaining balance. */
export function isOpenRouterAccountBalanceError(raw: string): boolean {
  return /openrouter_credits/i.test(raw) || /prompt tokens limit exceeded/i.test(raw);
}

/** Drop OpenRouter key-settings links before a chat error is shown or saved. */
export function stripOpenRouterUrls(message: string): string {
  return message
    .replace(/https?:\/\/openrouter\.ai\/\S+/gi, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([.,;:])/g, "$1")
    .trim();
}

/**
 * One-shot retry cap when OpenRouter rejects a reservation.
 * Null means surface the error instead of sending another request.
 * A prompt that already exceeds the remaining balance is not retried.
 */
export function affordableOutputRetryCap(
  errorMessage: string,
  currentMaxTokens: number,
  alreadyRetried: boolean,
): number | null {
  if (alreadyRetried) return null;
  if (/prompt tokens limit exceeded/i.test(errorMessage)) return null;
  const affordable = parseAffordableMaxTokens(errorMessage);
  if (affordable == null || affordable < MIN_AFFORDABLE_OUTPUT_TOKENS) return null;
  if (!(currentMaxTokens > affordable)) return null;
  return affordable;
}

function formatTokenCount(value: number): string {
  return value.toLocaleString("en-US");
}

/** User-facing copy for an OpenRouter output reservation. Null when this is not that error. */
export function describeProviderOutputLimit(raw: string): { title: string; message: string } | null {
  if (isOpenRouterAccountBalanceError(raw) || raw.includes(OPENROUTER_ACCOUNT_BALANCE_MESSAGE)) {
    return { title: "OpenRouter balance", message: OPENROUTER_ACCOUNT_BALANCE_MESSAGE };
  }

  const alreadyFriendly = /deeply smart credits are separate/i.test(raw) && !/https?:\/\//i.test(raw);
  const isReservation = /can only afford/i.test(raw) || /openrouter key's weekly limit/i.test(raw);
  if (!isReservation && !alreadyFriendly) return null;

  if (alreadyFriendly) {
    return { title: "Provider output limit", message: raw };
  }

  const affordable = parseAffordableMaxTokens(raw);
  const requestedMatch = raw.match(/requested up to\s+([\d,]+)/i);
  const requested = requestedMatch ? Number(requestedMatch[1].replace(/,/g, "")) : null;
  if (affordable != null && affordable < MIN_AFFORDABLE_OUTPUT_TOKENS) {
    return {
      title: "Provider output limit",
      message:
        "The OpenRouter key's weekly limit is almost used up, so this model cannot start a reply. Deeply Smart credits are separate from that key limit and are not used up.",
    };
  }
  const reserved =
    requested != null && Number.isFinite(requested)
      ? `This model reserved up to ${formatTokenCount(requested)} output tokens.`
      : "This model reserved more output tokens than the provider can cover.";
  const cover =
    affordable != null
      ? ` The OpenRouter key's weekly limit can only cover about ${formatTokenCount(affordable)} right now.`
      : " The OpenRouter key's weekly limit cannot cover that reservation.";
  return {
    title: "Provider output limit",
    message: `${reserved}${cover} Deeply Smart credits are separate from that key limit.`,
  };
}

export function isWebSearchRelatedError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("web search") ||
    lower.includes("web plugin") ||
    lower.includes(":online") ||
    lower.includes("openrouter:web_search") ||
    lower.includes("tool") && lower.includes("not support") ||
    lower.includes("provider returned error") ||
    lower.includes("provider not found") ||
    lower.includes("no available model provider")
  );
}
